import { useTranslation } from "react-i18next";
import { useDeferredValue, useEffect, useMemo, useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { AuthSession } from "../../auth/keycloak.js";
import { useFacilityNavParams } from "../facilities/drilldown/useFacilityNavParams.js";
import { useFacilities } from "../facilities/useFacilities.js";
import { groupRoles } from "./role-groups.js";
import { FilterField } from "./FilterField.js";
import { RoleTree } from "./RoleTree.js";
import { RoleEditor } from "./RoleEditor.js";
import { RolesForbiddenError, useRoles } from "./useRoles.js";

/**
 * Settings ▸ Manage roles (ADR-030 Phase A — the READ): the legacy two-pane screen. Left: the roles the caller may
 * administer, grouped by prefix. Right: the selected role. Every verdict (which roles, editability) comes from the
 * server; the tree of facilities is the caller's access-manage-scoped view.
 */
export function RolesView({ session, heading }: { session: AuthSession; heading?: ReactNode }) {
  const { t } = useTranslation();
  const roles = useRoles(session);
  const facilities = useFacilities(session, "access_manage");
  const flat = useMemo(() => facilities.data ?? [], [facilities.data]);
  // The selection IS the URL (`arc_role`, ADR-030 Phase D): a shareable deep link into one role, replace-written per
  // click. An id the list does not carry (another group's, hidden, gone) is dropped silently — never a 404 screen.
  const nav = useFacilityNavParams();
  const selectedId = nav.roleId;
  const groups = useMemo(() => groupRoles(roles.data?.roles ?? []), [roles.data]);
  const selected = roles.data?.roles.find((r) => r.id === selectedId) ?? null;
  const stale = roles.data !== undefined && selectedId !== null && selected === null;
  const [roleQuery, setRoleQuery] = useState("");
  const deferredRoleQuery = useDeferredValue(roleQuery);
  useEffect(() => {
    if (stale) nav.dropStale("arc_role");
  }, [stale, nav]);

  // The page heading (when the workspace hands it over) sits at the top of the TREE column, so it shares a row with
  // the selected role's name at the top of the editor column (owner). The single-message states keep it above.
  const withHeading = (body: ReactNode) => (
    <div className="flex flex-col gap-4">
      {heading}
      {body}
    </div>
  );
  if (roles.isPending) {
    return withHeading(
      <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("roles.loadingRoles")}
      </p>,
    );
  }
  if (roles.isError) {
    if (roles.error instanceof RolesForbiddenError) {
      return withHeading(<p className="text-muted-foreground max-w-prose text-sm">{t("roles.noRolesAdministered")}</p>);
    }
    return withHeading(
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="text-destructive text-sm">
          {t("roles.couldntLoadTheRoles")}
        </p>
        <Button variant="outline" size="sm" onPress={() => void roles.refetch()}>
          {t("roles.tryAgain")}
        </Button>
      </div>,
    );
  }
  if (roles.data.roles.length === 0) {
    return withHeading(<p className="text-muted-foreground max-w-prose text-sm">{t("roles.noRolesInGroup")}</p>);
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 md:flex-row">
      {/* The column wrapper carries the layout; the heading stays OUTSIDE the "Roles" navigation landmark (a page title
          inside a nav would read as the nav's title to assistive tech — panel), which wraps only the tree. */}
      <div className="border-border flex shrink-0 flex-col gap-4 md:w-72 md:overflow-y-auto md:border-r md:pr-3 lg:w-80">
        {heading}
        <FilterField label={t("roles.filterRoles")} placeholder={t("roles.filterRoles")} value={roleQuery} onChange={setRoleQuery} />
        <nav aria-label={t("roles.roles")}>
          <RoleTree groups={groups} selectedId={selectedId} onSelect={nav.selectRole} query={deferredRoleQuery} />
        </nav>
      </div>
      <section aria-labelledby={selected ? "role-editor-title" : "role-editor-empty"} className="min-w-0 flex-1 md:overflow-y-auto">
        {/* The editor reads grants and the base folder AGAINST the facility tree — a tree that has not arrived (or
            failed) must not be mistaken for "no such facility" / "outside your scope" (diff panel). */}
        {selected && facilities.isPending ? (
          <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("roles.loadingFacilities")}
          </p>
        ) : selected && facilities.isError ? (
          <div className="flex flex-col items-start gap-2">
            <p role="alert" className="text-destructive text-sm">
              {t("roles.couldntLoadTheFacilitiesThisRoles")}
            </p>
            <Button variant="outline" size="sm" onPress={() => void facilities.refetch()}>
              {t("roles.tryAgain")}
            </Button>
          </div>
        ) : selected ? (
          <RoleEditor key={selected.id} role={selected} callerIsSuper={roles.data.caller.isSuper} session={session} flat={flat} bypass={roles.data.caller.isSuper} />
        ) : (
          <p id="role-editor-empty" className="text-muted-foreground text-sm">
            {t("roles.selectARoleOnTheLeft")}
          </p>
        )}
      </section>
    </div>
  );
}
